import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { neutral } from '../../../styles/mui/colors';
import getFilterIcon from '../../../util/getFilterIcon';
import { parseSelectFilterOptions } from '../../../util/search';
import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

const labelWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
};

const formControlStyle = { margin: 0, width: '100%', justifyContent: 'space-between', padding: 0 };

const optionBoxStyle = {
  border: `1px solid ${neutral[100]}`,
  borderRadius: 1,
  px: 2,
  mt: 1,
  py: 1,
};

// RHF replacement for the previous Final-Form GroupOfFieldCheckboxes. All
// checkboxes write into a single array-valued field `name` (e.g. ['a','b']),
// which is exactly the value shape SelectMultipleFilter.handleSubmit consumes
// via values[name]. Toggling a checkbox adds/removes its option key.
const GroupOfFieldCheckboxes = ({ id, name, options }) => {
  const [showAll, setShowAll] = useState(false);
  const intl = useIntl();
  const { control } = useFormContext();

  const visibleOptions = showAll ? options : options.slice(0, 6);

  const sx = useMemo(() => ({ width: 1 }), []);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const selected = Array.isArray(field.value) ? field.value : [];
        const toggle = (key) => {
          const next = selected.includes(key)
            ? selected.filter((k) => k !== key)
            : [...selected, key];
          field.onChange(next);
        };
        return (
          <Box>
            {visibleOptions.map((option) => {
              const icon = option?.icon ? getFilterIcon(option.icon) : null;
              return (
                <Box key={option.key} sx={optionBoxStyle}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selected.includes(option.key)}
                        color="primary"
                        id={`${id}.${option.key}`}
                        onChange={() => toggle(option.key)}
                        sx={{ padding: 1 }}
                      />
                    }
                    label={
                      <Box sx={labelWrapperStyle}>
                        {icon}
                        <Typography sx={{ ml: 2 }} variant="body1">
                          {intl.formatMessage({ id: option.label })}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={formControlStyle}
                  />
                </Box>
              );
            })}

            {options.length > 6 ? (
              <Box display="flex" justifyContent="center">
                <Button
                  aria-controls={`${id}-options`}
                  aria-expanded={showAll}
                  endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreOutlinedIcon />}
                  onClick={() => setShowAll(!showAll)}
                  sx={sx}
                  variant="text"
                >
                  {showAll ? (
                    <FormattedMessage id="StoryblokArchiveBlog.showLess" />
                  ) : (
                    <FormattedMessage id="StoryblokArchiveBlog.showAll" />
                  )}
                </Button>
              </Box>
            ) : null}
          </Box>
        );
      }}
    />
  );
};

const getQueryParamName = (queryParamNames) => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

const SelectMultipleFilter = ({
  rootClassName = null,
  className = null,
  id,
  name,
  label,
  options,
  initialValues = {},
  contentPlacementOffset = 0,
  onSubmit,
  queryParamNames,
  searchMode = 'has_all',
  showAsPopup = false,
  applyFilters = () => null,
  filterConfig = {},
  ...rest
}) => {
  const intl = useIntl();

  const filterRef = useRef(null);
  const filterContentRef = useRef(null);

  const [contentStyle, setContentStyle] = useState({});

  const positionStyleForContent = useCallback(() => {
    if (filterRef.current && filterContentRef.current) {
      const distanceToRight = window.innerWidth - filterRef.current.getBoundingClientRect().right;
      const labelWidth = filterRef.current.offsetWidth;
      const contentWidth = filterContentRef.current.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;
      return { ...offset, ...minWidth };
    }
    return {};
  }, [contentPlacementOffset]);

  useEffect(() => {
    const style = positionStyleForContent();
    setContentStyle(style);
    // Optionally, you can add event listener for window resize to recalculate contentStyle
  }, [contentPlacementOffset, positionStyleForContent]);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];

  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilter.labelSelected' },
      { labelText: label, count: selectedOptions.length },
    )
    : label;

  const labelForPlain = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilterPlainForm.labelSelected' },
      { labelText: label, count: selectedOptions.length },
    )
    : label;

  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = (values) => {
    const usedValue = values ? values[`${name}`] : values;
    onSubmit(format(usedValue, queryParamName, searchMode));
  };

  return showAsPopup ? (
    <>
      <FilterPopup
        className={className}
        contentPlacementOffset={contentPlacementOffset}
        contentRef={filterContentRef}
        filterConfig={filterConfig}
        id={`${id}.popup`}
        initialValues={namedInitialValues}
        isSelected={hasInitialValues}
        keepDirtyOnReinitialize
        label={labelForPopup}
        name={name}
        onSubmit={handleSubmit}
        ref={filterRef}
        rootClassName={rootClassName}
        showAsPopup={showAsPopup}
        {...rest}
      >
        <GroupOfFieldCheckboxes id={`${id}-checkbox-group`} name={name} options={options} />
      </FilterPopup>
      {options.map((option) => (
        // Render h4 tags for SEO that are invisible but still part of the document
        <Typography component="h4" display="none" key={option.key}>
          {intl.formatMessage({ id: option.label })}
        </Typography>
      ))}
    </>
  ) : (
    <FilterPlain
      className={className}
      contentPlacementOffset={contentStyle}
      contentRef={filterContentRef}
      id={`${id}.plain`}
      initialValues={namedInitialValues}
      isSelected={hasInitialValues}
      label={labelForPlain}
      liveEdit
      onSubmit={handleSubmit}
      ref={filterRef}
      rootClassName={rootClassName}
      {...rest}
    >
      <GroupOfFieldCheckboxes id={`${id}-checkbox-group`} name={name} options={options} />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
